import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, CreditCard, KeyRound, HardDrive, Crown, BookOpen } from 'lucide-react';

function MoreMenuRow({ icon: Icon, title, description, onClick, disabled, locked, badge }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`w-full flex items-start gap-3 p-3 rounded-xl text-left transition-colors ${
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:bg-slate-50 dark:hover:bg-slate-800/80'
      }`}
    >
      <div className="w-10 h-10 shrink-0 rounded-full border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900">
        <Icon size={18} strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 leading-tight">{title}</span>
          {badge}
          {locked ? <Crown size={12} className="text-amber-500 shrink-0" aria-hidden /> : null}
        </div>
        {description ? (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug line-clamp-2">{description}</p>
        ) : null}
      </div>
    </button>
  );
}

function useHoverCapablePointer() {
  const [hoverCapable, setHoverCapable] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  });

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setHoverCapable(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  return hoverCapable;
}

export default function SidebarMoreMenu({
  showPlans = true,
  onOpenPlans,
  onOpenMyApi,
  myApiLocked = false,
  myApiActive = false,
  diskStorage = null,
  onOpenDiskInfo,
  onOpenReportApi,
}) {
  const { t } = useTranslation(['builder', 'profile']);
  const hoverCapable = useHoverCapablePointer();
  const [open, setOpen] = useState(false);
  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const flyoutRef = useRef(null);
  const closeTimerRef = useRef(null);
  const openedAtRef = useRef(0);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const markOpened = useCallback(() => {
    openedAtRef.current = Date.now();
  }, []);

  const scheduleClose = useCallback(() => {
    if (!hoverCapable) return;
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 180);
  }, [clearCloseTimer, hoverCapable]);

  const updateFlyoutPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panelWidth = Math.min(304, window.innerWidth - 88);
    let left = rect.right + 8;
    if (left + panelWidth > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - panelWidth - 8);
    }
    setFlyoutPos({
      top: Math.min(window.innerHeight - 24, Math.max(24, rect.top + rect.height / 2)),
      left,
    });
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    updateFlyoutPosition();
    markOpened();
    setOpen(true);
  }, [clearCloseTimer, updateFlyoutPosition, markOpened]);

  const closeMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
  }, [clearCloseTimer]);

  const handleEnter = useCallback(() => {
    if (!hoverCapable) return;
    openMenu();
  }, [hoverCapable, openMenu]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  useEffect(() => {
    if (!open) return undefined;
    updateFlyoutPosition();
    const onReflow = () => updateFlyoutPosition();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, updateFlyoutPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocPointer = (e) => {
      if (Date.now() - openedAtRef.current < 320) return;
      if (triggerRef.current?.contains(e.target)) return;
      if (flyoutRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDocPointer);
    return () => document.removeEventListener('pointerdown', onDocPointer);
  }, [open]);

  const handleTriggerClick = useCallback((e) => {
    e.stopPropagation();
    updateFlyoutPosition();
    if (open) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [open, updateFlyoutPosition, closeMenu, openMenu]);

  const diskVisible = diskStorage?.visible;
  let diskDesc = '';
  if (diskVisible) {
    if (diskStorage.unlimited) {
      diskDesc = t('profile:storageUnlimited', { defaultValue: 'Ilimitado no seu plano' });
    } else {
      diskDesc = t('builder:sidebarMoreDiskUsed', {
        defaultValue: '{{pct}}% do espaço em uso',
        pct: Math.round(diskStorage.storagePct ?? 0),
      });
    }
  }

  const flyoutPortal = open
    ? createPortal(
        <>
          {!hoverCapable ? (
            <button
              type="button"
              className="fixed inset-0 z-[20040] bg-slate-900/35 border-0 p-0 cursor-pointer"
              aria-label={t('builder:close', { defaultValue: 'Fechar' })}
              onClick={closeMenu}
            />
          ) : null}
          <div
            ref={flyoutRef}
            className="fixed z-[20050] pl-2"
            style={{
              top: flyoutPos.top,
              left: hoverCapable ? flyoutPos.left - 8 : flyoutPos.left,
              transform: 'translateY(-50%)',
            }}
            onMouseEnter={hoverCapable ? handleEnter : undefined}
            onMouseLeave={hoverCapable ? scheduleClose : undefined}
          >
            <div
              className="w-[min(19rem,calc(100vw-5.5rem))] rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl shadow-slate-900/10 dark:shadow-black/40 py-2 animate-in fade-in slide-in-from-left-2 duration-150"
              role="menu"
            >
              <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                  {t('builder:sidebarMoreTitle', { defaultValue: 'Mais opções' })}
                </p>
              </div>
              <div className="p-1.5 space-y-0.5">
                {showPlans ? (
                  <MoreMenuRow
                    icon={CreditCard}
                    title={t('builder:sidebarMorePlanTitle', { defaultValue: 'Contrate um Plano' })}
                    description={t('builder:sidebarMorePlanDesc', {
                      defaultValue: 'Planos, add-ons e upgrade da sua conta',
                    })}
                    onClick={() => {
                      onOpenPlans?.();
                      closeMenu();
                    }}
                  />
                ) : null}
                <MoreMenuRow
                  icon={KeyRound}
                  title={t('builder:myApiPanelTitle', { defaultValue: 'Minha API' })}
                  description={t('builder:sidebarMoreApiDesc', {
                    defaultValue: 'Chave do plugin Moodle e integrações',
                  })}
                  locked={myApiLocked}
                  onClick={() => {
                    onOpenMyApi?.();
                    closeMenu();
                  }}
                />
                {diskVisible ? (
                  <MoreMenuRow
                    icon={HardDrive}
                    title={t('profile:diskSpace', { defaultValue: 'Espaço em disco' })}
                    description={diskDesc}
                    onClick={() => {
                      onOpenDiskInfo?.();
                      closeMenu();
                    }}
                  />
                ) : null}
                <MoreMenuRow
                  icon={BookOpen}
                  title={t('builder:sidebarMoreReportApiTitle', { defaultValue: 'API do Relatório Geppetto' })}
                  description={t('builder:sidebarMoreReportApiDesc', {
                    defaultValue: 'Chamada JSON, exemplo e token do Moodle',
                  })}
                  onClick={() => {
                    onOpenReportApi?.();
                    closeMenu();
                  }}
                />
              </div>
            </div>
          </div>
        </>,
        document.body
      )
    : null;

  return (
    <>
      <div
        className="relative w-full"
        onMouseEnter={hoverCapable ? handleEnter : undefined}
        onMouseLeave={hoverCapable ? scheduleClose : undefined}
      >
        <button
          ref={triggerRef}
          type="button"
          onClick={handleTriggerClick}
          aria-expanded={open}
          aria-haspopup="true"
          className={`relative flex flex-col items-center justify-center w-full rounded-xl py-2 gap-1 transition-all duration-200 ${
            open || myApiActive
              ? 'text-blue-600 dark:text-blue-400 bg-blue-50/80 dark:bg-blue-900/40'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
          }`}
        >
          {(open || myApiActive) && (
            <div className="absolute left-[-8px] top-1/2 -translate-y-1/2 w-1 h-7 bg-blue-600 rounded-r-full" />
          )}
          <MoreHorizontal size={22} strokeWidth={2} />
          <span className="text-[10px] font-medium tracking-wide">
            {t('builder:sidebarMore', { defaultValue: 'Mais' })}
          </span>
        </button>
      </div>
      {flyoutPortal}
    </>
  );
}
